/* External libs */
import { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import {
  Button,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  TextInput,
  NumberInput,
  ColorInput,
  Radio,
  Divider,
  Textarea,
  Title,
  Breadcrumbs,
  Anchor,
} from "@mantine/core";
import { zodResolver } from "mantine-form-zod-resolver";
import { Link, useLocation, useNavigate } from "react-router-dom";

/* Constants */
import {
  ADMIN_ORGANIZATION,
  BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
  ORGANIZATIONS,
} from "_roles";

/* Types */
import { type BingoTemplate } from "bingo/types/bingo-template.model";

/* Hooks */
import { useDebug } from "common/debug/hooks/useDebug";
import { useOrganization } from "common/admin/auth/hooks";
import {
  useAddBingoTemplateMutation,
  useUpdateBingoTemplateMutation,
} from "bingo/api/bingo-template.api";

/* Contexts */
import {
  BingoTemplateFormProvider,
  useBingoTemplateForm,
} from "bingo/contexts/bingo-template.context";

/* Schemas */
import { bingoTemplateSchema } from "./BingoTemplateSchema";

/* Components */
import { AccessRights } from "common/admin/auth/AccessRights";
import FileInput from "common/admin/ui/FileInput";
import Collapsable from "common/admin/ui/Collapsable";
//import QueryWrapper from "common/components/QueryWrapper";
import { CreateGameTempHelper } from "../CreateGameTempHelper";
import { ADMIN_BINGO_PATH, ADMIN_PATH } from "_urls";

export function BingoTemplateAdminForm({
  bingoTemplate,
  editMode,
}: {
  bingoTemplate?: BingoTemplate;
  editMode: boolean;
}) {
  const { isDebugMode, log } = useDebug();
  const location = useLocation(); // ✅ Correct way to access the current path
  const isViewMode = location.pathname.endsWith("/duplicate");

  const navigate = useNavigate(); // ✅ Ensures navigation works correctly

  const [organization] = useOrganization();

  const [addBingoTemplate, addBingoTemplateResult] =
    useAddBingoTemplateMutation();
  const [updateBingoTemplate, updateBingoTemplateResult] =
    useUpdateBingoTemplateMutation();
  const [isLoading, setIsLoading] = useState(false);
  const {
    isError: isErrorAddBingoTemplateResult,
    error: errorAddBingoTemplateResult,
  } = addBingoTemplateResult;
  const {
    isError: isErrorUpdateBingoTemplateResult,
    error: errorUpdateBingoTemplateResult,
  } = updateBingoTemplateResult;

  useEffect(() => {
    if (isDebugMode) {
      if (isErrorAddBingoTemplateResult) {
        log(
          `errorAddBingoTemplateResult: ${JSON.stringify(
            errorAddBingoTemplateResult,
          )}`,
        );
      }
      if (isErrorUpdateBingoTemplateResult) {
        log(
          `errorUpdateBingoTemplateResult: ${JSON.stringify(
            errorUpdateBingoTemplateResult,
          )}`,
        );
      }
    }
  }, [
    isDebugMode,
    log,
    isErrorAddBingoTemplateResult,
    isErrorUpdateBingoTemplateResult,
    errorAddBingoTemplateResult,
    errorUpdateBingoTemplateResult,
  ]);

  const save = async () => {
    console.log(
      JSON.stringify(
        transformBingoTemplate({
          ...form.values,
        }),
      ),
    );

    const transformedData = transformBingoTemplate({
      ...form.values,
    });

    setIsLoading(true);

    // Check if we are in view mode
    console.log(`isViewMode: ${isViewMode}`);

    try {
      if (bingoTemplate && editMode && !isViewMode) {
        // Normal update logic
        const updateBingoTemplatePayload = await updateBingoTemplate({
          id: bingoTemplate.id,
          ...transformedData,
        });
        log(`Update successful: ${JSON.stringify(updateBingoTemplatePayload)}`);
      } else {
        // If in view mode, create a new template (POST request)
        const addBingoTemplatePayload =
          await addBingoTemplate(transformedData).unwrap();
        log(
          `New bingo template created: ${JSON.stringify(addBingoTemplatePayload)}`,
        );
      }
    } catch (error) {
      log(`Error: ${error}`);
    }

    setIsLoading(false);
    navigate(`../templates`);
  };

  function formatDimensions(dimensions: { width: number; height: number }) {
    if (!dimensions.width || !dimensions.height) {
      throw new Error("Object must contain 'width' and 'height' properties");
    }
    return `${dimensions.width}x${dimensions.height}`;
  }

  const initialFormValues = useCallback(() => {
    if (bingoTemplate && editMode) {
      return {
        access: {
          entity: bingoTemplate.access?.entity
            ? bingoTemplate.access.entity
            : "",
          organization: bingoTemplate.access?.organization
            ? bingoTemplate.access.organization
            : organization
              ? organization
              : "",
          section: bingoTemplate.access?.section
            ? bingoTemplate?.access.section
            : "",
        },
        creationTime: new Date(bingoTemplate.creationTime).toISOString(),
        name: bingoTemplate.name ? bingoTemplate.name : "",
        generalSettings: {
          bingoGridTitle: bingoTemplate.generalSettings?.bingoGridTitle
            ? bingoTemplate.generalSettings.bingoGridTitle
            : "",
          numbersRangeMin: bingoTemplate.generalSettings?.numbersRangeMin
            ? bingoTemplate.generalSettings.numbersRangeMin
            : 0,
          numbersRangeMax: bingoTemplate.generalSettings?.numbersRangeMax
            ? bingoTemplate.generalSettings.numbersRangeMax
            : 0,
          gridNumbersDisplay: bingoTemplate.generalSettings?.gridNumbersDisplay
            ? bingoTemplate.generalSettings.gridNumbersDisplay
            : "",
          winningNumberOffset: bingoTemplate.generalSettings
            ?.winningNumberOffset
            ? bingoTemplate.generalSettings.winningNumberOffset
            : 0,
          gridSelectionNumber: bingoTemplate.generalSettings
            ?.gridSelectionNumber
            ? bingoTemplate.generalSettings.gridSelectionNumber
            : 0,
          sharedScreenFormat: formatDimensions({
            width: bingoTemplate.generalSettings?.sharedScreenFormat?.width
              ? bingoTemplate.generalSettings.sharedScreenFormat.width
              : 0,
            height: bingoTemplate.generalSettings?.sharedScreenFormat?.height
              ? bingoTemplate.generalSettings.sharedScreenFormat.height
              : 0,
          }),
          textColor: bingoTemplate.generalSettings?.textColor
            ? bingoTemplate.generalSettings.textColor
            : "",
          alertColor: bingoTemplate.generalSettings?.alertColor
            ? bingoTemplate.generalSettings.alertColor
            : "",
        },
        frontOfficeMessages: {
          gameNotStarted: bingoTemplate.frontOfficeMessages?.gameNotStarted
            ? bingoTemplate.frontOfficeMessages.gameNotStarted
            : "",
          gameEnded: bingoTemplate.frontOfficeMessages?.gameEnded
            ? bingoTemplate.frontOfficeMessages.gameEnded
            : "",
          gameAlreadyStarted: bingoTemplate.frontOfficeMessages
            ?.gameAlreadyStarted
            ? bingoTemplate.frontOfficeMessages.gameAlreadyStarted
            : "",
          gameWelcome: bingoTemplate.frontOfficeMessages?.gameWelcome
            ? bingoTemplate.frontOfficeMessages.gameWelcome
            : "",
          gameWelcomeButtonLabel: bingoTemplate.frontOfficeMessages
            ?.gameWelcomeButtonLabel
            ? bingoTemplate.frontOfficeMessages.gameWelcomeButtonLabel
            : "",
          gridChoice: bingoTemplate.frontOfficeMessages?.gridChoice
            ? bingoTemplate.frontOfficeMessages.gridChoice
            : "",
          gridChoiceButtonLabel: bingoTemplate.frontOfficeMessages
            ?.gridChoiceButtonLabel
            ? bingoTemplate.frontOfficeMessages.gridChoiceButtonLabel
            : "",
          gameWaiting: bingoTemplate.frontOfficeMessages?.gameWaiting
            ? bingoTemplate.frontOfficeMessages.gameWaiting
            : "",
          drawExplainations: bingoTemplate.frontOfficeMessages
            ?.drawExplainations
            ? bingoTemplate.frontOfficeMessages.drawExplainations
            : "",
          notDrawnNumber: bingoTemplate.frontOfficeMessages?.notDrawnNumber
            ? bingoTemplate.frontOfficeMessages.notDrawnNumber
            : "",
          drawnNumber: bingoTemplate.frontOfficeMessages?.drawnNumber
            ? bingoTemplate.frontOfficeMessages.drawnNumber
            : "",
          drawnNumbersLabel: bingoTemplate.frontOfficeMessages
            ?.drawnNumbersLabel
            ? bingoTemplate.frontOfficeMessages.drawnNumbersLabel
            : "",
          winnerPrivate: bingoTemplate.frontOfficeMessages?.winnerPrivate
            ? bingoTemplate.frontOfficeMessages.winnerPrivate
            : "",
          looserPrivate: bingoTemplate.frontOfficeMessages?.looserPrivate
            ? bingoTemplate.frontOfficeMessages.looserPrivate
            : "",
          welcomeReturnButtonLabel: bingoTemplate.frontOfficeMessages
            ?.welcomeReturnButtonLabel
            ? bingoTemplate.frontOfficeMessages.welcomeReturnButtonLabel
            : "",
          winnerTooLatePrivate: bingoTemplate.frontOfficeMessages
            ?.winnerTooLatePrivate
            ? bingoTemplate.frontOfficeMessages.winnerTooLatePrivate
            : "",
        },
        publicScreenMessages: {
          welcome: bingoTemplate.publicScreenMessages?.welcome
            ? bingoTemplate.publicScreenMessages.welcome
            : "",
          drawInProgress: bingoTemplate.publicScreenMessages?.drawInProgress
            ? bingoTemplate.publicScreenMessages.drawInProgress
            : "",
          winnerCheck: bingoTemplate.publicScreenMessages?.winnerCheck
            ? bingoTemplate.publicScreenMessages.winnerCheck
            : "",
          endOfGame: bingoTemplate.publicScreenMessages?.endOfGame
            ? bingoTemplate.publicScreenMessages.endOfGame
            : "",
        },
        images: {
          mobileBackground: bingoTemplate.images?.mobileBackground
            ? bingoTemplate.images.mobileBackground
            : "",
          screenBackgroundSize1: bingoTemplate.images?.screenBackgroundSize1
            ? bingoTemplate.images.screenBackgroundSize1
            : "",
          screenBackgroundSize2: bingoTemplate.images?.screenBackgroundSize2
            ? bingoTemplate.images.screenBackgroundSize2
            : "",
          screenLogo: bingoTemplate.images?.screenLogo
            ? bingoTemplate.images.screenLogo
            : "",
        },
        privateScreenSettings: {
          messagesSize: bingoTemplate.privateScreenSettings?.messagesSize
            ? bingoTemplate.privateScreenSettings.messagesSize
            : "",
          messagesColor: bingoTemplate.privateScreenSettings?.messagesColor
            ? bingoTemplate.privateScreenSettings.messagesColor
            : "",
          buttonBackgroundColor: bingoTemplate.privateScreenSettings
            ?.buttonBackgroundColor
            ? bingoTemplate.privateScreenSettings.buttonBackgroundColor
            : "",
          buttonsLabelColor: bingoTemplate.privateScreenSettings
            ?.buttonsLabelColor
            ? bingoTemplate.privateScreenSettings.buttonsLabelColor
            : "",
          buttonsBorderColor: bingoTemplate.privateScreenSettings
            ?.buttonsBorderColor
            ? bingoTemplate.privateScreenSettings.buttonsBorderColor
            : "",
          buttonsActiveColor: bingoTemplate.privateScreenSettings
            ?.buttonsActiveColor
            ? bingoTemplate.privateScreenSettings.buttonsActiveColor
            : "",
          gridBgColor: bingoTemplate.privateScreenSettings?.gridBgColor
            ? bingoTemplate.privateScreenSettings.gridBgColor
            : "",
          gridNumberStandardColor: bingoTemplate.privateScreenSettings
            ?.gridNumberStandardColor
            ? bingoTemplate.privateScreenSettings.gridNumberStandardColor
            : "",
          gridNumberSuccessBgColor: bingoTemplate.privateScreenSettings
            ?.gridNumberSuccessBgColor
            ? bingoTemplate.privateScreenSettings.gridNumberSuccessBgColor
            : "",
          gridNumberFailureBgColor: bingoTemplate.privateScreenSettings
            ?.gridNumberFailureBgColor
            ? bingoTemplate.privateScreenSettings.gridNumberFailureBgColor
            : "",
          gridNumberColor: bingoTemplate.privateScreenSettings?.gridNumberColor
            ? bingoTemplate.privateScreenSettings.gridNumberColor
            : "",
          popinBgColor: bingoTemplate.privateScreenSettings?.popinBgColor
            ? bingoTemplate.privateScreenSettings.popinBgColor
            : "",
          popinTextColor: bingoTemplate.privateScreenSettings?.popinTextColor
            ? bingoTemplate.privateScreenSettings.popinTextColor
            : "",
        },
        publicScreenSettings: {
          messagesSize: bingoTemplate.publicScreenSettings?.messagesSize
            ? bingoTemplate.publicScreenSettings.messagesSize
            : "",
          messagesColor: bingoTemplate.publicScreenSettings?.messagesColor
            ? bingoTemplate.publicScreenSettings.messagesColor
            : "",
          screenTopMargin: bingoTemplate.publicScreenSettings?.screenTopMargin
            ? bingoTemplate.publicScreenSettings.screenTopMargin
            : "",
          screenLeftMargin: bingoTemplate.publicScreenSettings?.screenLeftMargin
            ? bingoTemplate.publicScreenSettings.screenLeftMargin
            : "",
        },
      };
    }
    return {
      access: {
        entity: "",
        organization: organization ? organization : "",
        section: "",
      },
      creationTime: new Date().toISOString(),
      name: "",
      generalSettings: {
        bingoGridTitle: "",
        numbersRangeMin: 0,
        numbersRangeMax: 0,
        gridNumbersDisplay: "",
        winningNumberOffset: 0,
        gridSelectionNumber: 0,
        sharedScreenFormat: {
          width: 0,
          height: 0,
        },
        textColor: "",
        alertColor: "",
      },
      frontOfficeMessages: {
        gameNotStarted: "",
        gameEnded: "",
        gameAlreadyStarted: "",
        gameWelcome: "",
        gameWelcomeButtonLabel: "",
        gridChoice: "",
        gridChoiceButtonLabel: "",
        gameWaiting: "",
        drawExplainations: "",
        notDrawnNumber: "",
        drawnNumber: "",
        drawnNumbersLabel: "",
        winnerPrivate: "",
        looserPrivate: "",
        welcomeReturnButtonLabel: "",
        winnerTooLatePrivate: "",
      },
      publicScreenMessages: {
        welcome: "",
        drawInProgress: "",
        winnerCheck: "",
        endOfGame: "",
      },
      images: {
        mobileBackground: "",
        screenBackgroundSize1: "",
        screenBackgroundSize2: "",
        screenLogo: "",
      },
      privateScreenSettings: {
        messagesSize: "",
        messagesColor: "",
        buttonBackgroundColor: "",
        buttonsLabelColor: "",
        buttonsBorderColor: "",
        buttonsActiveColor: "",
        gridBgColor: "",
        gridNumberStandardColor: "",
        gridNumberSuccessBgColor: "",
        gridNumberFailureBgColor: "",
        gridNumberColor: "",
        popinBgColor: "",
        popinTextColor: "",
      },
      publicScreenSettings: {
        messagesSize: "",
        messagesColor: "",
        screenTopMargin: "",
        screenLeftMargin: "",
      },
    };
  }, [bingoTemplate, editMode, organization]);

  const form = useBingoTemplateForm({
    initialValues: initialFormValues(),
    validate: zodResolver(bingoTemplateSchema),
  });

  const isDirty = useMemo(
    () => !isEqual(bingoTemplate, form.values),
    [bingoTemplate, form],
  );
  interface SharedScreenFormat {
    width: number;
    height: number;
  }

  function parseSharedScreenFormat(format: string): SharedScreenFormat {
    const [width, height] = format.split("x").map(Number);
    return { width, height };
  }

  function transformBingoTemplate(input: any): any {
    return {
      access: {
        entity: input.access.entity || "",
        organization: input.access.organization || "",
        section: input.access.section || "",
      },
      creationTime: input.creationTime,
      name: input.name,
      generalSettings: {
        bingoGridTitle: input.generalSettings.bingoGridTitle,
        numbersRangeMin: input.generalSettings.numbersRangeMin,
        numbersRangeMax: input.generalSettings.numbersRangeMax,
        gridNumbersDisplay: input.generalSettings.gridNumbersDisplay,
        winningNumberOffset: input.generalSettings.winningNumberOffset,
        gridSelectionNumber: input.generalSettings.gridSelectionNumber,
        sharedScreenFormat: parseSharedScreenFormat(
          input.generalSettings.sharedScreenFormat,
        ),
        textColor: input.generalSettings.textColor,
        alertColor: input.generalSettings.alertColor,
      },
      frontOfficeMessages: input.frontOfficeMessages,
      publicScreenMessages: input.publicScreenMessages,
      images: input.images,
      privateScreenSettings: input.privateScreenSettings,
      publicScreenSettings: input.publicScreenSettings,
    };
  }

  return (
    <>
      {isLoading ? (
        <div className="h-100 w-100 d-flex align-items-center justify-content-center">
          <div className="spinner-border" role="status">
            <LoadingOverlay visible={true} overlayOpacity={100} />
          </div>
        </div>
      ) : null}

      <BingoTemplateFormProvider form={form}>
        <Stack>
          <div
            className="sticky top-0 z-50 pb-4"
            style={{ backgroundColor: "#E9ECEF" }}
          >
            <Title order={3} align="center" m="xs">
              {bingoTemplate?.name || "New Bingo template"}
            </Title>
            <Breadcrumbs separator="→">
              <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                Admin
              </Anchor>

              <Anchor
                component={Link}
                to={`/${ADMIN_PATH}/${ADMIN_BINGO_PATH}`}
              >
                Bingo
              </Anchor>

              <Anchor
                component={Link}
                to={`/${ADMIN_PATH}/${ADMIN_BINGO_PATH}/templates`}
              >
                Bingo Templates
              </Anchor>
            </Breadcrumbs>
            <Group position={"right"}>
              <Button
                className="bg-button-admin disabled:opacity-75"
                disabled={!isDirty}
                onClick={() => {
                  save();
                }}
              >
                Save
              </Button>
            </Group>
          </div>
          {bingoTemplate && editMode && !isViewMode && (
            <Collapsable title={"Create new Bingo Game"}>
              <Group>
                <CreateGameTempHelper bingoTemplate={bingoTemplate} />
              </Group>
            </Collapsable>
          )}
          <Group className="grid  grid-cols-2">
            {organization === ADMIN_ORGANIZATION && (
              <AccessRights
                authorizedOrganizations={
                  BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                }
                authorizedRoles={BINGO_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES}
              >
                <Select
                  sx={{ flex: 1 }}
                  label="Organization"
                  required
                  data={ORGANIZATIONS}
                  {...form.getInputProps("access.organization")}
                />
              </AccessRights>
            )}
            <TextInput
              required
              sx={{ flex: 1 }}
              label={"Title"}
              {...form.getInputProps("name")}
            />
          </Group>

          {/* General Settings */}
          <Collapsable title="General settings">
            <Group className="grid grid-rows-5 grid-cols-1">
              <Group className="grid grid-cols-2">
                <TextInput
                  label="Bingo Grid Title"
                  {...form.getInputProps("generalSettings.bingoGridTitle")}
                  required
                />
              </Group>
              <Group className="grid grid-cols-5">
                <TextInput
                  label="Grid Number Display"
                  {...form.getInputProps("generalSettings.gridNumbersDisplay")}
                  required
                />
                <div></div>

                <NumberInput
                  label="Min Range"
                  {...form.getInputProps("generalSettings.numbersRangeMin")}
                  required
                />
                <NumberInput
                  label="Max Range"
                  {...form.getInputProps("generalSettings.numbersRangeMax")}
                  required
                />

                <NumberInput
                  label="Offset"
                  {...form.getInputProps("generalSettings.winningNumberOffset")}
                  required
                />
              </Group>
              <Group className="grid grid-cols-5">
                <NumberInput
                  label="Grid Selection Number"
                  {...form.getInputProps("generalSettings.gridSelectionNumber")}
                  required
                />
              </Group>
              <Group className="grid grid-cols-2">
                <Radio.Group
                  label="Shared Screen Format "
                  {...form.getInputProps("generalSettings.sharedScreenFormat")}
                  required
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Radio value="1540x220" label="1540x220" />
                  <Radio value="1600x900" label="16/9" />
                </Radio.Group>
              </Group>

              <Group className="grid grid-cols-4">
                <ColorInput
                  label="Text Color"
                  {...form.getInputProps("generalSettings.textColor")}
                  required
                />
                <div></div>
                <ColorInput
                  label="Alert Color"
                  {...form.getInputProps("generalSettings.alertColor")}
                  required
                />
                <div></div>
              </Group>
            </Group>
          </Collapsable>
          {/* Front Office Messages */}
          <Collapsable title="Messages Front Office">
            <Group className="grid grid-rows-6 grid-cols-1">
              <TextInput
                label="Game Not Started"
                required
                {...form.getInputProps("frontOfficeMessages.gameNotStarted")}
              />
              <TextInput
                label="Game Ended"
                required
                {...form.getInputProps("frontOfficeMessages.gameEnded")}
              />
              <TextInput
                label="Game Already Started"
                required
                {...form.getInputProps(
                  "frontOfficeMessages.gameAlreadyStarted",
                )}
              />
              <Textarea
                label="Game Welcome"
                required
                {...form.getInputProps("frontOfficeMessages.gameWelcome")}
              />
              <TextInput
                label="Game Welcome Button Label"
                required
                {...form.getInputProps(
                  "frontOfficeMessages.gameWelcomeButtonLabel",
                )}
              />
              <Divider />
              <TextInput
                label="Grid Choice"
                required
                {...form.getInputProps("frontOfficeMessages.gridChoice")}
              />
              <TextInput
                label="Grid Choice Button Label"
                required
                {...form.getInputProps(
                  "frontOfficeMessages.gridChoiceButtonLabel",
                )}
              />
              <Divider />
              <Textarea
                label="Game Waiting"
                required
                {...form.getInputProps("frontOfficeMessages.gameWaiting")}
              />
              <Textarea
                label="Draw Explanations"
                required
                {...form.getInputProps("frontOfficeMessages.drawExplainations")}
              />
              <TextInput
                label="Not Drawn Number"
                required
                {...form.getInputProps("frontOfficeMessages.notDrawnNumber")}
              />
              <TextInput
                label="Drawn Number"
                required
                {...form.getInputProps("frontOfficeMessages.drawnNumber")}
              />
              <TextInput
                label="Drawn Numbers Label"
                required
                {...form.getInputProps("frontOfficeMessages.drawnNumbersLabel")}
              />
              <Textarea
                label="Winner Private"
                required
                {...form.getInputProps("frontOfficeMessages.winnerPrivate")}
              />
              <Textarea
                label="Loser Private"
                required
                {...form.getInputProps("frontOfficeMessages.looserPrivate")}
              />
              <TextInput
                label="Welcome Return Button Label"
                required
                {...form.getInputProps(
                  "frontOfficeMessages.welcomeReturnButtonLabel",
                )}
              />
              <Textarea
                label="Winner Too Late Private"
                required
                {...form.getInputProps(
                  "frontOfficeMessages.winnerTooLatePrivate",
                )}
              />
            </Group>
          </Collapsable>
          {/* Public Screen Messages */}
          <Collapsable title="Messages Screen Public">
            <Group className="grid grid-rows-4 grid-cols-1">
              <Textarea
                label="Welcome"
                {...form.getInputProps("publicScreenMessages.welcome")}
                required
              />
              <Textarea
                label="Draw In Progress"
                {...form.getInputProps("publicScreenMessages.drawInProgress")}
                required
              />
              <Textarea
                label="Winner Check"
                {...form.getInputProps("publicScreenMessages.winnerCheck")}
                required
              />
              <Textarea
                label="End Of Game"
                {...form.getInputProps("publicScreenMessages.endOfGame")}
                required
              />
            </Group>
          </Collapsable>
          {/* Images */}
          <Collapsable title="Images">
            <Group className="grid grid-rows-4 grid-cols-1">
              <FileInput
                label={"Mobile Background"}
                {...form.getInputProps("images.mobileBackground")}
                required
              />
              <FileInput
                label={"Screen Background Size 1"}
                {...form.getInputProps("images.screenBackgroundSize1")}
                required
              />
              <FileInput
                label={"Screen Background Size 2"}
                {...form.getInputProps("images.screenBackgroundSize2")}
                required
              />
              <FileInput
                label={"Screen Logo"}
                {...form.getInputProps("images.screenLogo")}
                required
              />
            </Group>
          </Collapsable>
          {/* Private Screen Settings */}
          <Collapsable title="Pcracticipants Screen Settings">
            <Group className="grid grid-rows-1 grid-cols-3">
              <TextInput
                label="Messages Size"
                placeholder="e.g. 16px, 1.2rem"
                className="w-[65%]"
                {...form.getInputProps("privateScreenSettings.messagesSize")}
                required
              />
            </Group>

            {/* Color Pickers */}
            <Group className="grid grid-rows-4 grid-cols-3">
              <ColorInput
                label="Messages Color"
                {...form.getInputProps("privateScreenSettings.messagesColor")}
                required
              />
              <ColorInput
                label="Button Background Color"
                {...form.getInputProps(
                  "privateScreenSettings.buttonBackgroundColor",
                )}
                required
              />
              <ColorInput
                label="Button Label Color"
                {...form.getInputProps(
                  "privateScreenSettings.buttonsLabelColor",
                )}
                required
              />
              <ColorInput
                label="Button Border Color"
                {...form.getInputProps(
                  "privateScreenSettings.buttonsBorderColor",
                )}
                required
              />
              <ColorInput
                label="Button Active Color"
                {...form.getInputProps(
                  "privateScreenSettings.buttonsActiveColor",
                )}
                required
              />
              <ColorInput
                label="Grid Background Color"
                {...form.getInputProps("privateScreenSettings.gridBgColor")}
                required
              />
              <ColorInput
                label="Grid Number Standard Color"
                {...form.getInputProps(
                  "privateScreenSettings.gridNumberStandardColor",
                )}
                required
              />
              <ColorInput
                label="Grid Number Success Background"
                {...form.getInputProps(
                  "privateScreenSettings.gridNumberSuccessBgColor",
                )}
                required
              />
              <ColorInput
                label="Grid Number Failure Background"
                {...form.getInputProps(
                  "privateScreenSettings.gridNumberFailureBgColor",
                )}
                required
              />
              <ColorInput
                label="Grid Number Color"
                {...form.getInputProps("privateScreenSettings.gridNumberColor")}
                required
              />
              <Group className="grid grid-cols-2">
                <ColorInput
                  label="Popin Background Color"
                  format="rgba"
                  className="-mt-4"
                  {...form.getInputProps("privateScreenSettings.popinBgColor")}
                />
                <Button
                  className="bg-button-admin mt-8"
                  onClick={() =>
                    form.setFieldValue(
                      "privateScreenSettings.popinBgColor",
                      "transparent",
                    )
                  }
                >
                  Transparent
                </Button>
              </Group>

              <ColorInput
                label="Popin Text Color"
                {...form.getInputProps("privateScreenSettings.popinTextColor")}
                required
              />
            </Group>
          </Collapsable>
          {/* Public Screen Settings */}
          <Collapsable title="Public Screen Settings">
            <Group className="grid grid-rows-2 grid-cols-4">
              {/* Messages Color */}
              <ColorInput
                label="Messages Color"
                {...form.getInputProps("publicScreenSettings.messagesColor")}
              />
              {/* Messages Size */}
              <TextInput
                label="Messages Size"
                placeholder="e.g. 16px, 1.2rem"
                required
                className="w-[65%]"
                {...form.getInputProps("publicScreenSettings.messagesSize")}
              />
              <div></div>
              <div></div>

              {/* Screen Top Margin */}
              <TextInput
                label="Screen Top Margin"
                placeholder="e.g. 10px, 5%"
                required
                className="w-[65%]"
                {...form.getInputProps("publicScreenSettings.screenTopMargin")}
              />

              {/* Screen Left Margin */}
              <TextInput
                label="Screen Left Margin"
                required
                className="w-[65%]"
                placeholder="e.g. 5px, 2rem"
                {...form.getInputProps("publicScreenSettings.screenLeftMargin")}
              />
            </Group>
          </Collapsable>
        </Stack>
      </BingoTemplateFormProvider>
    </>
  );
}
